/**
 * MultiCloudFlowSection
 * Section wrapper for the multi-cloud flow visualization
 * Shows intelligent load balancing and failover across Claude API providers
 */

import { motion } from 'framer-motion';
import { Shield, Shuffle, DollarSign, Activity } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MultiCloudFlowVisualization } from '@/components/flow/MultiCloudFlowVisualization';
import { containerVariants, itemVariants } from '@/components/animations/variants';
import type { LucideIcon } from 'lucide-react';

interface FeatureItemProps {
  icon: LucideIcon;
  children: React.ReactNode;
}

function FeatureItem({ icon: Icon, children }: FeatureItemProps) {
  return (
    <li className="flex items-center gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <span className="text-sm font-medium">{children}</span>
    </li>
  );
}

export function MultiCloudFlowSection() {
  return (
    <section className="py-16 lg:py-24 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Multi-Cloud Routing</h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Intelligent load balancing and automatic failover across Claude API providers
          </p>
        </motion.div>

        {/* Card Container */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="backdrop-blur-md bg-background/50 border border-border/50 rounded-xl p-6 md:p-10 shadow-xl"
        >
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 items-center">
            {/* Left: Content */}
            <motion.div variants={itemVariants} className="space-y-6">
              <Badge variant="secondary">Pro &amp; Enterprise</Badge>

              <h3 className="text-2xl font-bold lg:text-3xl">
                Intelligent Load Balancing
              </h3>

              <p className="text-muted-foreground">
                Route requests to Claude across multiple cloud providers for maximum
                reliability and cost efficiency. When one provider has issues,
                Pathfinder automatically fails over to another.
              </p>

              <ul className="space-y-3">
                <FeatureItem icon={Shield}>
                  Automatic failover
                  <Badge variant="outline" className="ml-2 text-xs">
                    Pro
                  </Badge>
                </FeatureItem>
                <FeatureItem icon={Shuffle}>Round-robin distribution</FeatureItem>
                <FeatureItem icon={DollarSign}>
                  Cost optimization
                  <Badge variant="outline" className="ml-2 text-xs">
                    Enterprise
                  </Badge>
                </FeatureItem>
                <FeatureItem icon={Activity}>Real-time health monitoring</FeatureItem>
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button asChild>
                  <a href="/pricing">See Pricing</a>
                </Button>
                <Button variant="ghost" asChild>
                  <a href="https://cal.com/naivana/demo">Start Free Trial &rarr;</a>
                </Button>
              </div>
            </motion.div>

            {/* Right: Flow Visualization */}
            <motion.div variants={itemVariants} className="flex justify-center">
              <MultiCloudFlowVisualization autoAnimate cycleInterval={3500} />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
